/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { AsyncSubject } from "rxjs";
import { Injectable } from "@angular/core";

import Logger from "../../../core/libs/logger";
import util from "../../jscdk/util";
import { OsModel } from "@html-core";
import { clientUtil } from "@html-core";
import { BaseViewClientModel } from "./baseviewclient-model";
import { ClientIdService } from "./clientid-model";
import { HorizonClientInfo } from "../service/client-info.service";
import { PreDataSetModel } from "./pre-data-set-model";
import { PolicyModel } from "../../../shared/google-policy/policy-model";
import { FeatureConfigs } from "./feature-configs";
import { useragentUtil, BrowserName, ClientOS } from "../../../core/libs/useragent-util";
import UserActiveTimeSyncCtrl from "../../jscdk/timer/userActiveTimeSyncCtrl";
/**
 * Most of data in viewClientModel can be read directly, except 'client info'.
 * For both chrome and html client, client info is init async.
 * As a result, it should return promise when other module tries to get client
 * info from viewClientModel.
 */
@Injectable({
   providedIn: "root"
})
export class ViewClientModel extends BaseViewClientModel {
   public buildNum: string = __BUILD_NUMBER__;
   public enableDownloadInstaller: boolean = true;
   public nativeInstallerLink: string =
      "https://customerconnect.omnissa.com/downloads/info/slug/desktop_end_user_computing/omnissa_horizon_clients/8";
   public platform: string = "Unknown";

   public mIsIE: boolean = false;
   public mIsEdge: boolean = false;
   public mIsChrome: boolean = false;
   public mIsWebKit: boolean = false;
   public mIsOpera: boolean = false;
   public mIsGecko: boolean = false;
   public mIsSafari: boolean = false;
   public mIsIPhone: boolean = false;
   public mIsIOSSafari: boolean = false;
   public mIsAirWatchBrowser: boolean = false;
   public mIsTouchDevice: boolean = false;
   public mIsMacOS: boolean = false;
   public mIsAndroid: boolean = false;
   public mIsIOS: boolean = false;
   public mIsVrDevice: boolean = false;

   public browserName: string;
   public mBrowserVersionMain: number = 0;
   public mBrowserVersionFull: string;
   public mVersionWebKitMain: number;
   public mVersionWebKitFull: string;

   public disableReconnectForWS1: boolean = false;
   public disableCEIP: boolean = false;
   public contextPath: string = null;

   public clientInfoSubject = new AsyncSubject();

   public enableCredentialCleanupForHTMLAccess: boolean = false;
   public clientHideServerInformation: boolean = false;
   public clientHideDomainList: boolean = false;
   public isAnonymousMode: boolean = false;

   public osModel: OsModel = null;
   public machineName: string = null;
   public osLiteral: string;
   public csrfCheck: boolean = false;
   public disableIPv6: boolean = false;
   public enableKeyMapping: boolean = true;
   public useWindowReplacementApi: boolean = true;
   public enterFullscreenIfOneMonitor: boolean = true;
   public AssetID: string = null;
   public SerialNum: string = null;
   public useAssetIdReplaceMachineName = false;
   public cdrEntry;
   public ipAddress: string = null;
   public instanceId: string = null;
   public enableNetworkIndicator: boolean = true;
   public networkStateTcpRttMSLow: number;
   public networkStateTcpRttMSHigh: number;
   public networkStateQualityScoreTcpThresholdGood: number;
   public networkStateQualityScoreTcpThresholdPoor: number;
   public networkStateRttCheckPeriodMs: number;
   public enableRTAVH264Codec: boolean = true;
   public enableRTAVOpusCodec: boolean = true;
   public enableRTAVDTX: boolean = true;
   public enableExtendedAPI: boolean = false;
   public hardwareAccelerationOption: string = "no-preference";
   public enableBlastCodec: boolean = false;
   public disableHeartBeat: boolean = false;
   public RDPVCBridgeSupported: boolean = false;
   constructor(
      private clientIdService: ClientIdService,
      private preDataSetModel: PreDataSetModel,
      private policyModel: PolicyModel,
      private featureConfigs: FeatureConfigs
   ) {
      super(clientUtil.getClientType());
      this.clientID = this.clientIdService.getClientID();
      this.osModel = new OsModel();
      this.instanceId = this.clientIdService.generateUID();
   }

   public initClientInfo = async (info: HorizonClientInfo) => {
      // Do NOT change the init order here
      this.initOSInfo(info);
      this.initBrowserInfo();
      this.initBrowserName();
      this.initPlatform();
      this.initRebrandInfo(info);
      this.enableRTAVH264Codec = info.enableRTAVH264Codec;
      this.enableRTAVOpusCodec = info.enableRTAVOpusCodec;
      this.enableRTAVDTX = info.enableRTAVDTX;
      this.enableExtendedAPI = info.enableExtendedAPI;
      this.hardwareAccelerationOption = info.hardwareAccelerationOption;
      this.disableHeartBeat = info.disableHeartBeat;
      this.ipAddress = info.ipAddress;
      Logger.setLogLevel(info.logLevel);
      Logger.debug(
         "[RTAV]: video hardware acceleration option value set to " +
            this.hardwareAccelerationOption +
            "in viewClientModel"
      );
      if (clientUtil.isChromeOrIwaClient()) {
         this.initPolicy();
         this.RDPVCBridgeSupported = true;
         this.enableRTAVH264Codec = await this._getEnableRTAVH264CodecFromPolicy();
         this.enableRTAVOpusCodec = await this._getEnableRTAVOpusCodecFromPolicy();
         this.enableRTAVDTX = await this._getEnableRTAVDTXFromPolicy();
         this.hardwareAccelerationOption = await this._getHardwareAccelerationOptionFromPolicy();
         Logger.debug(
            "[RTAV]: video hardware acceleration option value set to " +
               this.hardwareAccelerationOption +
               "in chrome client viewClientModel"
         );
         this.enableBlastCodec = await this._getEnableBlastCodec();
         Logger.debug(
            "[BlastCodec]: Blast Codec Kill Switch set to " + this.enableBlastCodec + "in chrome client viewClientModel"
         );
      } else {
         this.enableBlastCodec = info.enableBlastCodec && (this.mIsChrome || this.mIsEdge);
      }
      this.logLevel = info.logLevel;
      this.disableReconnectForWS1 = info.disableReconnectForWS1;
      this.disableCEIP = info.disableCEIP;
      this.acceptLanguage = info.acceptLanguage;
      this.contextPath = info.contextPath;
      this.csrfCheck = info.csrfCheck;
      this.disableIPv6 = info.disableIPv6;
      this.enableKeyMapping = info.enableKeyMapping;
      this.useWindowReplacementApi = !this.mIsAirWatchBrowser && info.useWindowReplacementApi;
      this.enterFullscreenIfOneMonitor = info.enterFullscreenIfOneMonitor;
      this.enableNetworkIndicator = info.enableNetworkIndicator;
      this.networkStateTcpRttMSLow = info.networkStateTcpRttMSLow;
      this.networkStateTcpRttMSHigh = info.networkStateTcpRttMSHigh;
      this.networkStateQualityScoreTcpThresholdGood = info.networkStateQualityScoreTcpThresholdGood;
      this.networkStateQualityScoreTcpThresholdPoor = info.networkStateQualityScoreTcpThresholdPoor;
      this.networkStateRttCheckPeriodMs = info.networkStateRttCheckPeriodMs;
      this.featureConfigs.setConfig("KillSwitch-WasmBlast", this.enableBlastCodec);
      if (info.vrCheck !== undefined) {
         //Only when the flag is set, use the flag value, only take effect on Linux
         //If there is no flag value, use the API result
         Logger.debug("There is kill-switch for VR from server: " + info.vrCheck);
         // In case to impact the experience on Windows/Mac/Chromebook, only includes Linux and Android
         this.mIsVrDevice = info.vrCheck && (this.platform === "Linux" || this.platform === "Android");
      } else {
         Logger.debug("No kill-switch for VR from server.");
      }
      clientUtil.isVrDevice = this.mIsVrDevice;

      /*
       * User is able to customize native installers download links
       * in the configuration file.
       *
       * If no download links are customized in the configuration file,
       * use the default download link: https://customerconnect.omnissa.com/downloads/info/slug/desktop_end_user_computing/omnissa_horizon_clients/8
       */
      this.enableDownloadInstaller = info.enableDownloadInstaller;
      this.nativeInstallerLink = info.installerLink;

      util.l10N.setLocaleAndLoad(this.acceptLanguage);
      if (this.disableCEIP) {
         this.preDataSetModel.setDataValue("dataSharingAllowed", false);
      }

      this.clientInfoSubject.next(true);
      this.clientInfoSubject.complete();

      Logger.debug("HTML5 Running Env = \n" + this._LoggedInfo());
   };

   private _LoggedInfo = () => {
      // Only add necessary message without privacy
      const message = {
         buildNum: this.buildNum,
         ua: useragentUtil.getUserAgent(),
         vs: window.navigator.appVersion.toString(),
         acceptLanguage: this.acceptLanguage,
         rebrandEnable: this.rebrandEnable
      };
      return JSON.stringify(message);
   };

   private _getEnableRTAVH264CodecFromPolicy = (): Promise<boolean> => {
      return new Promise((resolve, reject) => {
         this.policyModel.getModel().then((model) => {
            if (!model) {
               resolve(true);
            } else {
               if (
                  model.getCommonAdminSettings()?.enableRTAVH264Codec === false ||
                  model.getCommonAdminSettings()?.enableRTAVH264Codec === true
               ) {
                  resolve(model.getCommonAdminSettings().enableRTAVH264Codec);
               } else {
                  resolve(true);
               }
            }
         });
      });
   };

   private _getEnableRTAVOpusCodecFromPolicy = (): Promise<boolean> => {
      return new Promise((resolve, reject) => {
         this.policyModel.getModel().then((model) => {
            if (!model) {
               resolve(true);
            } else {
               if (
                  model.getCommonAdminSettings()?.enableRTAVOpusCodec === false ||
                  model.getCommonAdminSettings()?.enableRTAVOpusCodec === true
               ) {
                  resolve(model.getCommonAdminSettings().enableRTAVOpusCodec);
               } else {
                  resolve(true);
               }
            }
         });
      });
   };

   private _getEnableRTAVDTXFromPolicy = (): Promise<boolean> => {
      return new Promise((resolve, reject) => {
         this.policyModel.getModel().then((model) => {
            if (!model) {
               resolve(true);
            } else {
               if (
                  model.getCommonAdminSettings()?.enableRTAVDTX === false ||
                  model.getCommonAdminSettings()?.enableRTAVDTX === true
               ) {
                  resolve(model.getCommonAdminSettings().enableRTAVDTX);
               } else {
                  resolve(true);
               }
            }
         });
      });
   };

   private _getHardwareAccelerationOptionFromPolicy = (): Promise<string> => {
      return new Promise((resolve, reject) => {
         this.policyModel.getModel().then((model) => {
            if (!model) {
               resolve("no-preference");
            } else {
               if (
                  model.getCommonAdminSettings()?.hardwareAccelerationOption === "prefer-hardware" ||
                  model.getCommonAdminSettings()?.hardwareAccelerationOption === "prefer-software"
               ) {
                  resolve(model.getCommonAdminSettings().hardwareAccelerationOption);
               } else {
                  resolve("no-preference");
               }
            }
         });
      });
   };

   private _getEnableBlastCodec = (): Promise<boolean> => {
      return new Promise((resolve, reject) => {
         this.policyModel.getModel().then((model) => {
            if (!model) {
               resolve(false);
            } else {
               if (
                  model.getCommonAdminSettings()?.enableBlastCodec === false ||
                  model.getCommonAdminSettings()?.enableBlastCodec === true
               ) {
                  resolve(model.getCommonAdminSettings().enableBlastCodec);
               } else {
                  resolve(false);
               }
            }
         });
      });
   };

   public onFailReadingClientInfo = () => {
      /**
       * Since VADC don't have info.jsp, and BSG is only allow access using
       * specified address for info.jsp, it's possible of failing to get the
       * info.jsp. When that happens, the client will continue int work
       * as before.
       * Don't forget the init work here!
       */

      this.initBrowserInfo();
      this.initBrowserName();
      this.initPlatform();

      this.clientInfoSubject.next(true);
      this.clientInfoSubject.complete();
   };

   public initChromeDeviceInfo = (chromeDeviceInfo) => {
      if (chromeDeviceInfo.AssetID) {
         this.AssetID = chromeDeviceInfo.AssetID;
      }
      if (chromeDeviceInfo.SerialNum) {
         this.SerialNum = chromeDeviceInfo.SerialNum;
      }
   };

   private initBrowserName = () => {
      if (this.osModel.mIsAndroid) {
         this.browserName = "An";
      } else if (this.osModel.mIsIOS) {
         this.browserName = "IOS";
      } else if (this.mIsEdge) {
         this.browserName = "Edge";
      } else if (this.mIsIE) {
         // mobile browsers should not enter here or after
         this.browserName = "IE";
      } else if (this.osModel.mIsChromeOS) {
         this.browserName = "CRX";
      } else if (this.mIsChrome) {
         this.browserName = "Chr";
      } else if (this.mIsWebKit) {
         this.browserName = "Saf";
      } else if (this.mIsOpera) {
         this.browserName = "Op";
      } else if (this.mIsGecko) {
         // webkit will not enter here
         this.browserName = "FF";
      } else {
         this.browserName = "UNK";
      }
   };

   private initBrowserInfo = () => {
      // Detect browser version.
      // the browser and version detection copied from original code,
      // but it's better that we can have a browser service to do this job
      const _browser: string = useragentUtil.getBrowserName();

      this.mIsIE = _browser == BrowserName.IE;

      // Before adopt the new API, this.mIsWebKit = ua.indexOf("applewebkit") > -1 && ua.indexOf("chrome") === -1;
      // mIsWebKit is only true on Safari browser
      this.mIsWebKit = this.mIsSafari = _browser == BrowserName.SAFARI;
      this.mIsOpera = _browser == BrowserName.OPERA;
      this.mIsChrome = _browser == BrowserName.CHROME;
      this.mIsEdge = _browser == BrowserName.CHROMIUMEDGE;

      // See:
      // https://developer.mozilla.org/en/Gecko_user_agent_string_reference
      // Also, Webkit says it's 'like Gecko', so we get a false positive
      // here.
      // mIsGecko means firefox browser
      this.mIsGecko = _browser == BrowserName.FIREFOX;

      if (this.osModel.mIsIOS) {
         // even chrome on iOS has 'safari' in user agent,
         // but chrome uses 'crios'.
         this.mIsIOSSafari = _browser == BrowserName.SAFARI;
         this.mIsIPhone = useragentUtil.isIphone() ? true : false;
      } else {
         this.mIsIOSSafari = false;
      }

      this.mIsAirWatchBrowser = _browser == BrowserName.AIRWATCH;

      this.mIsTouchDevice = this.osModel.mIsIOS || this.osModel.mIsAndroid;
      this.mIsMacOS = this.osModel.mIsMacOS;
      this.mIsAndroid = this.osModel.mIsAndroid;
      this.mIsIOS = this.osModel.mIsIOS;
      this.mIsVrDevice = !!(
         //@ts-ignore
         ((window.navigator.xr && !this.mIsChrome && !this.mIsEdge && !this.mIsOpera) || useragentUtil.isMobileVr())
      );
      useragentUtil.getBrowserVersionFull().then((version) => {
         this.mBrowserVersionFull = version;
         this.mBrowserVersionMain = Number(this.mBrowserVersionFull.split(".")[0]);
      });
      //applewebkit version is AppleWebKit/605.1.15, but jscdk uses Version/17.5, so keeping store seperately
      this.mVersionWebKitFull = this.mIsWebKit ? useragentUtil.getWebkitVersion() : null;
      this.mVersionWebKitMain = this.mVersionWebKitFull ? Number(this.mVersionWebKitFull.split(".")[0]) : null;
   };

   private initPlatform = () => {
      //'client-build for blast portal'
      if (!!this.osModel.mIsWin32 || !!this.osModel.mIsWin64 || !!this.osModel.mIsWinMobile) {
         this.platform = "Windows";
      } else if (!!this.osModel.mIsLinux32 || !!this.osModel.mIsLinux64) {
         this.platform = "Linux";
      } else if (this.osModel.mIsIOS) {
         this.platform = "iOS";
      } else if (this.osModel.mIsMacOS) {
         this.platform = "Mac";
      } else if (this.osModel.mIsChromeOS) {
         this.platform = "ChromeBook";
      } else if (this.osModel.mIsAndroid) {
         this.platform = "Android";
      }
      this.machineName = this.browserName + ":" + this.platform;
   };

   private initOSInfo = (data: HorizonClientInfo) => {
      this.osModel.detectOS(data.os);
      this.osLiteral = this.osModel.getOsLiteral(data.os);
   };

   private initRebrandInfo = (data: HorizonClientInfo) => {
      if (data.rebrandName) {
         this.rebrandName = data.rebrandName;
         this.rebrandEnable = true;
      } else {
         this.rebrandEnable = false;
      }

      if (data.rebrandHelpUrl) {
         this.rebrandHelpUrl = data.rebrandHelpUrl;
         // Add https:// to fix bug 2177688
         if (this.rebrandHelpUrl.indexOf("://") === -1) {
            this.rebrandHelpUrl = "https://" + this.rebrandHelpUrl;
         }
      }

      // below link seems not used for now.
      if (data.rebrandGotoForumUrl) {
         this.rebrandGotoForumUrl = data.rebrandGotoForumUrl;
      }
   };

   private initPolicy() {
      this.policyModel.getModel().then((model) => {
         if (model?.getCommonAdminSettings()?.useAssetIdReplaceMachineName) {
            this.useAssetIdReplaceMachineName = true;
            Logger.info("Enable useAssetIdReplaceMachineName configuration.");
         }
      });
   }
}
